'use client';

import clsx from 'clsx';
import { useEffect, useState } from 'react';

import { Button, RichText, Typography } from '@/components/core';
import { MicrocopyStoryblok } from '@/types/storyblok/storyblok-types';

import { CookieListItem } from './components/CookieListItem/CookieListItem';
import { useCookieStore } from './store/cookieStore';

import styles from './CookiePopup.module.scss';

type CookiePopupProps = {
    translations: MicrocopyStoryblok;
};

export const CookiePopup: React.FC<CookiePopupProps> = ({ translations }) => {
    const [isHydrated, setIsHydrated] = useState(false);
    const { cookiePopupShown, setCookiePopupShown, setCookieData } = useCookieStore();
    const [showManageCookiesModal, setShowManageCookiesModal] = useState(false);
    const [cookiePreference, setCookiePreference] = useState(false);
    const [cookieAnalytics, setCookieAnalytics] = useState(false);
    const [cookieAdvertising, setCookieAdvertising] = useState(false);

    useEffect(() => {
        useCookieStore.persist.rehydrate();
        setIsHydrated(true);
    }, []);

    if (!isHydrated || cookiePopupShown) {
        return null;
    }

    return (
        <>
            {!showManageCookiesModal && (
                <div className={styles.root}>
                    <Typography className={styles.title} as="h3">{translations.cookieManageTitle}</Typography>
                    <RichText document={translations.cookieDescription} />
                    <div className={styles.wrapper}>
                        <Button
                            className="secondaryButton"
                            onClick={() => setShowManageCookiesModal(true)}
                        >
                            {translations.cookieManage}
                        </Button>

                        <div className={styles.buttons}>
                            <Button
                                hideArrow
                                className="secondaryButton"
                                onClick={() => {
                                    setCookieData({
                                        necessary: true,
                                        preferences: false,
                                        analytics: false,
                                        advertising: false,
                                    });
                                    setCookiePopupShown(true);
                                }}
                            >
                                {translations.cookieButtonTextReject}
                            </Button>

                            <Button
                                onClick={() => {
                                    setCookieData({
                                        necessary: true,
                                        preferences: true,
                                        analytics: true,
                                        advertising: true,
                                    });
                                    setCookiePopupShown(true);
                                }}
                            >
                                {translations.cookieButtonTextAccept}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showManageCookiesModal && (
                <div className={clsx(styles.manageCookiesOverlay, 'bodyNoScroll')}>
                    <div className={styles.manageCookiesContainer}>
                        <Typography
                            as="h3"
                            className={styles.manageCookiesTitle}
                        >
                            {translations.cookieManageTitle}
                        </Typography>

                        <div className={styles.manageCookieItems}>
                            <CookieListItem
                                title={translations.cookieNecessaryTitle}
                                description={translations.cookieNecessaryDescription}
                                // eslint-disable-next-line react/jsx-boolean-value
                                value={true}
                                disabledSwitch
                            />

                            <CookieListItem
                                title={translations.cookiePreferenceTitle}
                                description={translations.cookiePreferenceDescription}
                                value={cookiePreference}
                                onChange={value => setCookiePreference(value)}
                            />

                            <CookieListItem
                                title={translations.cookieAnalyticsTitle}
                                description={translations.cookieAnalyticsDescription}
                                value={cookieAnalytics}
                                onChange={value => setCookieAnalytics(value)}
                            />

                            <CookieListItem
                                title={translations.cookieAdvertisementTitle}
                                description={translations.cookieAdvertisementDescription}
                                value={cookieAdvertising}
                                onChange={value => setCookieAdvertising(value)}
                            />

                            <div className={styles.scrollOverlay}></div>
                        </div>

                        <div className={styles.manageCookieButtons}>
                            <div
                                className={styles.backButton}
                                onClick={() => {
                                    setShowManageCookiesModal(false);
                                }}
                            >
                                <Button
                                    hideArrow
                                    onClick={() => {
                                        setShowManageCookiesModal(false);
                                    }}
                                >
                                    {translations.back}
                                </Button>
                            </div>

                            <Button
                                className="strongButton"
                                onClick={() => {
                                    setCookieData({
                                        necessary: true,
                                        preferences: cookiePreference,
                                        analytics: cookieAnalytics,
                                        advertising: cookieAdvertising,
                                    });
                                    setCookiePopupShown(true);
                                    setShowManageCookiesModal(false);
                                }}
                            >
                                {translations.save}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
